import type { MaterialIntent } from '../constants/materialIntent';
import {
  materialIntentNavigationState,
  materialIntentRoute,
} from '../constants/materialIntent';
import { buildCourseMaterialPath } from '../constants/courseStudioRoutes';
import { parseSessionDetail } from '../lib/parseSessionDetail';
import { parseSessionNotes } from '../lib/sessionNotesHelpers';
import type { PlanSession, PlanUnit } from './coursePlan';
import { resolveSessionConfig } from '../lib/hitoArchetypeUi';
import type {
  LinkedSessionContext,
  LinkedSessionFlowStep,
  LinkedSessionRef,
} from '@interactive-room';

export type { LinkedSessionContext, LinkedSessionRef };

/** Título del hito sin prefijos administrativos ("Hito 1:", etc.). */
export function cleanLinkedSessionTitle(title: string): string {
  return String(title || '')
    .trim()
    .replace(/^hito\s+\d+\s*:\s*/i, '')
    .replace(/^sesi[oó]n\s+\d+\s*:\s*/i, '')
    .replace(/^(?:unidad|sesi[oó]n|objetivo(?:\s+de\s+sesi[oó]n)?)\s*:\s*/i, '')
    .trim();
}

export type { LinkedSessionContext, LinkedSessionRef, LinkedSessionFlowStep };

export function mapParsedFlowToSessionFlow(
  flow: import('../lib/parseSessionDetail').SessionFlowStep[],
): LinkedSessionFlowStep[] {
  return flow.map((step) => ({
    title: step.title,
    ...(step.durationMin != null ? { durationMin: step.durationMin } : {}),
    ...(step.durationLabel ? { durationLabel: step.durationLabel } : {}),
    ...(step.roles.teacher ? { teacher: step.roles.teacher } : {}),
    ...(step.roles.students ? { students: step.roles.students } : {}),
    ...(step.roles.materials ? { materials: step.roles.materials } : {}),
  }));
}

/** Brief pedagógico rico — intención por paso (no solo títulos de la ruta). */
export function buildSessionPedagogicalBrief(
  flow: LinkedSessionFlowStep[],
  evidence?: string,
): string {
  if (!flow.length) return '';

  const lines = ['Flujo pedagógico de la sesión (cada paso → un bloque de guía alineado):'];

  flow.forEach((step, index) => {
    const duration = step.durationLabel ? ` ${step.durationLabel}` : '';
    lines.push('', `${index + 1}. ${step.title}${duration}`);
    if (step.teacher) lines.push(`   Docente: ${step.teacher}`);
    if (step.students) lines.push(`   Estudiantes: ${step.students}`);
    if (step.materials) lines.push(`   Materiales: ${step.materials}`);
  });

  if (evidence?.trim()) {
    lines.push('', `Evidencia de avance: ${evidence.trim()}`);
  }

  return lines.join('\n').trim();
}

export function buildSessionSummaryFromDetail(rawContent: string): string {
  const parsed = parseSessionDetail(rawContent);
  const parts: string[] = [];

  if (parsed.summary?.trim() && !parsed.learningObjective) {
    parts.push(parsed.summary.trim());
  }

  if (parsed.flow.length > 0) {
    const brief = buildSessionPedagogicalBrief(
      mapParsedFlowToSessionFlow(parsed.flow),
      parsed.evidence,
    );
    if (brief) parts.push(brief);
  } else if (parsed.evidence?.trim()) {
    parts.push(`Evidencia de avance: ${parsed.evidence.trim()}`);
  }

  return parts.join('\n\n').trim();
}

export function buildLinkedSessionContext(params: {
  courseId: string;
  unit: PlanUnit;
  session: PlanSession;
  subjectTag?: string;
  level?: string;
}): LinkedSessionContext {
  const { courseId, unit, session, subjectTag, level } = params;
  const detail = session.detail?.trim() ?? '';
  const parsed = detail ? parseSessionDetail(detail) : null;
  const objectiveFromDetail = parsed?.learningObjective ?? parsed?.summary;
  const notesParsed = parseSessionNotes(session.notes);
  const sessionObjective =
    objectiveFromDetail?.trim() ||
    notesParsed.foco?.trim() ||
    session.title.trim() ||
    'Sesión planificada';

  const sessionSummary =
    detail.length > 0
      ? buildSessionSummaryFromDetail(detail)
      : [session.title.trim(), notesParsed.foco, notesParsed.evidencia].filter(Boolean).join('\n');

  const sessionFlow =
    parsed?.flow?.length ? mapParsedFlowToSessionFlow(parsed.flow) : undefined;

  return {
    courseId,
    unitId: unit.id,
    sessionId: session.id,
    unitTitle: unit.title.trim() || unit.objective?.trim() || 'Unidad',
    sessionTitle: session.title.trim() || 'Sesión',
    sessionObjective,
    sessionSummary: sessionSummary || sessionObjective,
    ...(sessionFlow?.length ? { sessionFlow } : {}),
    ...(unit.curriculumTarget ? { curriculumTarget: unit.curriculumTarget } : {}),
    ...(unit.generationMode ? { generationMode: unit.generationMode } : {}),
    ...(session.curriculumSlice ? { curriculumSlice: session.curriculumSlice } : {}),
    sessionConfig: resolveSessionConfig(session),
    ...(subjectTag ? { subjectTag } : {}),
    ...(level ? { level } : {}),
  };
}

/** Semilla pedagógica para el textarea del modal (foco + flujo con intención + evidencia). */
export function buildMaterialSeedFromLinkedSession(context: LinkedSessionContext): string {
  const parts: string[] = [];
  const foco = context.sessionObjective?.trim();
  if (foco) parts.push(foco);

  if (context.sessionFlow?.length) {
    parts.push(buildSessionPedagogicalBrief(context.sessionFlow));
  } else {
    const summary = context.sessionSummary?.trim();
    if (summary && summary !== foco) {
      parts.push(summary);
    }
  }

  return parts.join('\n\n').trim();
}

export function buildSessionMaterialNavigation(
  subjectId: string,
  courseId: string,
  intent: MaterialIntent,
  context: LinkedSessionContext,
): { path: string; state: ReturnType<typeof materialIntentNavigationState> } {
  const path = buildCourseMaterialPath(courseId, materialIntentRoute(intent, subjectId, courseId));
  return {
    path,
    state: materialIntentNavigationState(intent, context),
  };
}

export function linkedSessionRefFromContext(context: LinkedSessionContext): LinkedSessionRef {
  return {
    courseId: context.courseId,
    unitId: context.unitId,
    sessionId: context.sessionId,
  };
}

export function matchesLinkedSession(
  ref: LinkedSessionRef | undefined,
  courseId: string,
  unitId: string,
  sessionId: string,
): boolean {
  if (!ref) return false;
  return ref.courseId === courseId && ref.unitId === unitId && ref.sessionId === sessionId;
}
